import { useRef, useState, useEffect } from "react";
import {
  Search, Bell, Heart, MessageSquare, Bookmark, MoreHorizontal, MapPin,
  Coffee, Bus, HelpCircle, Users, MessageCircle, Star,
  Plus, ChevronRight, Rocket,
} from "lucide-react";
import { Reveal } from "@/lib/reveal";
import { SectionMarker } from "@/components/SectionMarker";

const TABS = ["For you", "Nearby", "Travel", "Transport", "Health & Care", "Events"];

const POSTS = [
  {
    id: "sarah", name: "Sarah M.", initials: "SM", grad: "linear-gradient(135deg,#00D2B4,#00F0FF)",
    cat: "Accessible place", catColor: "#00F0FF", time: "2h",
    text: "Found this café today — wheelchair accessible entrance, plenty of space between tables and accessible bathroom.",
    place: "Wellington", likes: 24, comments: 8, liked: true, Icon: Coffee,
  },
  {
    id: "michael", name: "Michael T.", initials: "MT", grad: "linear-gradient(135deg,#3B82F6,#8B5CF6)",
    cat: "Share an experience", catColor: "#8B5CF6", time: "4h",
    text: "Travelling on the bus today was much easier because the driver understood my accessibility needs.",
    likes: 31, comments: 12, liked: true, Icon: Bus,
  },
  {
    id: "leilani", name: "Leilani K.", initials: "LK", grad: "linear-gradient(135deg,#8B5CF6,#00D2B4)",
    cat: "Ask the community", catColor: "#F59E0B", time: "6h",
    text: "Has anyone had a good accessibility experience at Auckland Airport?",
    likes: 9, comments: 16, liked: false,
  },
];

const SHARE_OPTIONS = [
  { title: "Share an experience", desc: "Share something you've experienced.", Icon: MessageCircle, color: "#00F0FF" },
  { title: "Make a recommendation", desc: "Recommend somewhere or something that could help.", Icon: Star, color: "#8B5CF6" },
  { title: "Share a place", desc: "Share an accessible place others may find useful.", Icon: MapPin, color: "#00D2B4" },
  { title: "Ask the community", desc: "Ask about an accessibility experience or situation.", Icon: HelpCircle, color: "#F59E0B" },
];

const Avatar = ({ initials, grad, size = "h-10 w-10" }) => (
  <span
    className={`flex ${size} shrink-0 items-center justify-center rounded-full font-display text-xs font-bold text-navy-900`}
    style={{ background: grad, boxShadow: "0 0 18px -8px rgba(0,240,255,0.7)" }}
    aria-hidden="true"
  >
    {initials}
  </span>
);

const CatBadge = ({ label, color }) => (
  <span
    className="rounded-full px-2 py-0.5 text-[10px] font-medium"
    style={{ color, background: `${color}1f`, border: `1px solid ${color}40` }}
  >
    {label}
  </span>
);

const PostCard = ({ p, testid }) => (
  <article
    className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 transition-colors hover:border-teal/25"
    data-testid={testid}
  >
    <div className="flex items-start gap-3">
      <Avatar initials={p.initials} grad={p.grad} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-display text-sm font-semibold text-white">{p.name}</span>
          <CatBadge label={p.cat} color={p.catColor} />
          <span className="text-xs text-slatemuted">· {p.time}</span>
          <MoreHorizontal className="ml-auto h-4 w-4 text-slatemuted" aria-hidden="true" />
        </div>

        <div className="mt-2 flex gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-slate-200 leading-relaxed">{p.text}</p>
            {p.place && (
              <p className="mt-2 inline-flex items-center gap-1 text-xs text-slatemuted">
                <MapPin className="h-3.5 w-3.5 text-teal" aria-hidden="true" /> {p.place}
              </p>
            )}
          </div>
          {p.Icon && (
            <span
              className="hidden sm:flex h-16 w-20 shrink-0 items-center justify-center rounded-xl border border-white/10"
              style={{ background: "radial-gradient(circle at 35% 25%, rgba(0,210,180,0.22), rgba(10,19,34,0.5) 75%)" }}
              aria-hidden="true"
            >
              <p.Icon className="h-6 w-6 text-teal" />
            </span>
          )}
        </div>

        <div className="mt-3 flex items-center gap-5 text-xs text-slatemuted">
          <span className="inline-flex items-center gap-1.5">
            <Heart className={`h-4 w-4 ${p.liked ? "fill-teal text-teal" : ""}`} aria-hidden="true" /> {p.likes}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MessageSquare className="h-4 w-4" aria-hidden="true" /> {p.comments}
          </span>
          <Bookmark className="ml-auto h-4 w-4" aria-hidden="true" />
        </div>
      </div>
    </div>
  </article>
);

const FeedPanel = () => (
  <div
    className="rounded-3xl border border-white/10 p-4 sm:p-5 backdrop-blur"
    style={{ background: "linear-gradient(180deg, rgba(14,28,51,0.55), rgba(10,19,34,0.4))" }}
    data-testid="community-feed"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-display text-base font-bold tracking-wide text-white">AIGA COMMUNITY</p>
        <p className="text-xs text-slatemuted">What&apos;s happening in the community</p>
      </div>
      <div className="flex items-center gap-3 text-slate-300">
        <Search className="h-[18px] w-[18px]" aria-hidden="true" />
        <span className="relative">
          <Bell className="h-[18px] w-[18px]" aria-hidden="true" />
          <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-teal text-[9px] font-bold text-navy-900">3</span>
        </span>
      </div>
    </div>

    {/* tabs */}
    <div className="mt-4 flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
      {TABS.map((t, i) => (
        <span
          key={t}
          className={`shrink-0 text-sm ${i === 0 ? "font-semibold text-teal" : "text-slatemuted"}`}
        >
          {t}
          {i === 0 && <span className="mt-1.5 block h-0.5 w-full rounded-full bg-teal" />}
        </span>
      ))}
    </div>

    <div className="mt-3 space-y-3">
      {POSTS.map((p, i) => (
        <PostCard key={p.id} p={p} testid={`community-post-${i + 1}`} />
      ))}
    </div>
  </div>
);

const SharePrompt = () => (
  <button
    type="button"
    className="group flex w-full items-center gap-4 rounded-2xl border border-teal/30 p-5 text-left backdrop-blur-sm transition-colors hover:border-teal/60"
    style={{ background: "linear-gradient(120deg, rgba(0,210,180,0.10), rgba(10,19,34,0.4) 65%)", boxShadow: "0 0 40px -18px rgba(0,240,255,0.6)" }}
    data-testid="community-share-prompt"
  >
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-teal/40 bg-teal/10">
      <Plus className="h-6 w-6 text-teal" aria-hidden="true" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block font-display text-lg font-semibold text-teal">What would you like to share?</span>
      <span className="mt-0.5 block text-sm text-slate-300">
        Share an experience, recommendation, place or ask the community.
      </span>
    </span>
    <ChevronRight className="h-5 w-5 shrink-0 text-teal transition-transform group-hover:translate-x-1" aria-hidden="true" />
  </button>
);

const PhonePreview = () => (
  <div
    className="mx-auto w-full max-w-[300px] rounded-[2.4rem] border border-white/12 bg-navy-900 p-3 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.85)]"
    style={{ background: "linear-gradient(180deg, rgba(14,28,51,0.9), rgba(8,15,28,0.95))" }}
    data-testid="community-phone-preview"
    aria-label="Preview of the AIGA Community mobile app share sheet"
  >
    <div className="rounded-[2rem] border border-white/8 bg-navy-900/60 px-4 pb-5 pt-3">
      {/* status bar */}
      <div className="flex items-center justify-between text-[10px] text-slatemuted">
        <span>9:41</span>
        <span className="h-1.5 w-16 rounded-full bg-white/15" aria-hidden="true" />
      </div>
      <p className="mt-4 text-[11px] font-mono uppercase tracking-[0.2em] text-teal">Aiga Community</p>
      <p className="mt-1.5 font-display text-xl font-bold leading-tight text-white">What would you like to share?</p>
      <p className="mt-1.5 text-xs text-slate-300">Share an experience, recommendation, place or ask the community.</p>

      <ul className="mt-4 space-y-2.5">
        {SHARE_OPTIONS.map((o) => (
          <li key={o.title}>
            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{ background: `${o.color}1f`, border: `1px solid ${o.color}44` }}
              >
                <o.Icon className="h-[18px] w-[18px]" style={{ color: o.color }} aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-white">{o.title}</span>
                <span className="block text-[11px] leading-snug text-slatemuted">{o.desc}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-slatemuted" aria-hidden="true" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export const AigaCommunity = () => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || revealed) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { rootMargin: "-100px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [revealed]);

  return (
    <section id="community" className="relative py-16 lg:py-24 overflow-hidden" data-testid="community-section">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-1/4 h-[460px] w-[520px] rounded-full bg-aigablue/10 blur-[150px]" />
        <div className="absolute left-0 bottom-0 h-[380px] w-[420px] rounded-full bg-teal/10 blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-14 items-start">
          {/* left / intro */}
          <div className="lg:col-span-5">
            <Reveal>
              <SectionMarker n="04" label="Aiga Community" />
              <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.08]">
                <span className="block">Real experiences.</span>
                <span className="block">Useful recommendations.</span>
                <span className="block text-teal">Shared knowledge.</span>
              </h2>
              <p className="mt-5 text-base text-slate-300 leading-relaxed">
                AIGA Community is being developed as a space where people can discover accessibility
                places, share experiences and recommendations, ask questions and help others navigate
                the world.
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="mt-7">
                <SharePrompt />
              </div>
            </Reveal>
          </div>

          {/* right / product preview */}
          <div ref={ref} className={`lg:col-span-7 ${revealed ? "reveal-in" : ""}`}>
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
              <div className="reveal-item flex-1" style={{ "--reveal-from": "translateY(24px)", "--reveal-delay": "120ms" }}>
                <FeedPanel />
              </div>
              <div className="reveal-item mx-auto w-full max-w-[240px] sm:mx-0 sm:w-[220px] sm:shrink-0" style={{ "--reveal-from": "translateY(24px)", "--reveal-delay": "260ms" }}>
                <PhonePreview />
              </div>
            </div>
          </div>
        </div>

        {/* compact A-ID + Community connection + status */}
        <Reveal delay={0.1}>
          <div
            className="mt-8 flex flex-col gap-4 rounded-2xl border border-white/10 p-4 sm:p-5 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between"
            style={{ background: "linear-gradient(120deg, rgba(0,210,180,0.07), rgba(10,19,34,0.4) 65%)" }}
            data-testid="community-alongside-aid"
          >
            <div className="flex items-center gap-3.5">
              <span className="flex shrink-0 items-center gap-1.5" aria-hidden="true">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-teal/40 font-display text-[9px] font-bold text-teal" style={{ boxShadow: "0 0 18px -8px rgba(0,240,255,0.6)" }}>A-ID</span>
                <span className="text-teal/60">·</span>
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-teal/40 bg-teal/5">
                  <Heart className="h-4 w-4 text-teal" />
                </span>
                <span className="text-aigablue/60">·</span>
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-aigablue/40">
                  <Users className="h-[18px] w-[18px] text-aigablue" />
                </span>
              </span>
              <p className="text-sm text-slate-300 leading-relaxed">
                A-ID connects your accessibility information with the shared knowledge of AIGA Community.
              </p>
            </div>
            <span
              className="inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-teal/30 bg-teal/5 px-3.5 py-1.5 text-xs font-mono uppercase tracking-[0.18em] text-teal sm:self-auto"
              data-testid="community-status"
            >
              <Rocket className="h-3.5 w-3.5" aria-hidden="true" /> Coming soon
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
